/**
 * AI Checker API 客户端
 *
 * 封装 Cloudflare Worker API 调用逻辑
 */

import { WORKER_URL, API_TIMEOUT } from '@constants/aiChecker.js';

/**
 * AI Checker API 类
 */
export class AiCheckerApi {
  /**
   * 构造函数
   * @param {string} workerUrl - Worker URL（可选，默认使用配置）
   */
  constructor(workerUrl = WORKER_URL) {
    this.workerUrl = workerUrl;
    this.timeout = API_TIMEOUT;
  }

  /**
   * 规范化 URL
   * @param {string} url - 输入 URL
   * @returns {string} 规范化后的 URL
   */
  normalizeUrl(url) {
    const trimmed = url.trim();
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      return `https://${trimmed}`;
    }
    return trimmed;
  }

  /**
   * 验证 URL 格式
   * @param {string} url - URL 字符串
   * @returns {boolean} 是否有效
   */
  isValidUrl(url) {
    try {
      new URL(this.normalizeUrl(url));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 带超时的 fetch
   * @param {string} url - 请求 URL
   * @param {RequestInit} options - fetch 选项
   * @returns {Promise<Response>} fetch 响应
   */
  async fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('请求超时，请稍后重试');
      }
      throw error;
    }
  }

  /**
   * 扫描网站 AI 可读性
   * @param {string} url - 要扫描的网站 URL
   * @param {object} options - 可选配置
   * @param {number} options.timeout - 请求超时时间（毫秒）
   * @param {AbortSignal} options.signal - 可取消请求的 signal
   * @returns {Promise<object>} 扫描结果
   */
  async scan(url, options = {}) {
    const { timeout = this.timeout, signal } = options;

    // 验证 URL
    if (!this.isValidUrl(url)) {
      throw new Error('请输入有效的 URL 格式');
    }

    // 规范化 URL
    const normalizedUrl = this.normalizeUrl(url);

    // 构建请求 URL
    const requestUrl = new URL(this.workerUrl);
    requestUrl.searchParams.set('url', normalizedUrl);

    // 添加时间戳避免缓存（用于开发调试）
    if (import.meta.env.DEV) {
      requestUrl.searchParams.set('t', Date.now().toString());
    }

    // 发起请求
    try {
      const response = await this.fetchWithTimeout(requestUrl.toString(), {
        signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `扫描失败 (${response.status})`);
      }

      const result = await response.json();

      // 检查 API 返回的错误
      if (result.error) {
        throw new Error(result.message || result.error);
      }

      return result;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('请求已取消');
      }
      throw error;
    }
  }

  /**
   * 格式化时间戳
   * @param {string} isoString - ISO 时间字符串
   * @returns {string} 格式化后的时间
   */
  static formatTimestamp(isoString) {
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diff = now - date;

      // 小于 1 分钟
      if (diff < 60000) {
        return 'Just now';
      }

      // 小于 1 小时
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      }

      // 小于 1 天
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }

      // 显示日期
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  }

  /**
   * 映射 API 结果到前端状态
   * @param {object} apiResult - API 返回结果
   * @returns {object} 前端状态对象
   */
  static mapApiResultToState(apiResult) {
    const { details, overall } = apiResult;
    const llmsScore = Number(details?.llmsTxt?.score || 0);
    const llmsMax = Number(details?.llmsTxt?.maxScore || 0);
    const discoverabilityScore = Number(details?.discoverability?.score || 0);
    const discoverabilityMax = Number(details?.discoverability?.maxScore || 0);
    const schemaScore = Number(details?.schemaOrg?.score || 0);
    const schemaMax = Number(details?.schemaOrg?.maxScore || 0);
    const endpointScore = Number(details?.dataEndpoints?.score || 0);
    const endpointMax = Number(details?.dataEndpoints?.maxScore || 0);

    return {
      scanResults: {
        llmsTxt: AiCheckerApi.getStatusInfo(
          details.llmsTxt.exists ? 'detected' : 'missing',
          details.llmsTxt.hasFull ? 'full' : 'basic'
        ),
        schemaOrg: AiCheckerApi.getSchemaStatusInfo(details.schemaOrg),
        ndjson: AiCheckerApi.getEndpointStatusInfo(details.dataEndpoints),
        robots: AiCheckerApi.getStatusInfo(
          details.discoverability.hasRobots ? 'detected' : 'missing'
        ),
        sitemap: AiCheckerApi.getStatusInfo(
          details.discoverability.hasSitemap ? 'detected' : 'missing'
        ),
      },
      aiReadinessScore: overall.score,
      scoreDetails: {
        // Discovery 维度统一为 llms + robots/sitemap（可发现性）
        discovery: AiCheckerApi.getScoreDetail(
          llmsScore + discoverabilityScore,
          llmsMax + discoverabilityMax
        ),
        structure: AiCheckerApi.getScoreDetail(
          schemaScore,
          schemaMax
        ),
        machineReadability: AiCheckerApi.getScoreDetail(
          endpointScore,
          endpointMax
        ),
      },
      recommendations: apiResult.recommendations || [],
      timestamp: apiResult.timestamp,
    };
  }

  /**
   * 获取状态显示信息
   * @param {string} status - 状态 (missing/detected/incomplete)
   * @param {string} variant - 变体 (full/basic)
   * @returns {object} 状态信息对象
   */
  static getStatusInfo(status, variant = 'basic') {
    const statusMap = {
      missing: {
        status: 'missing',
        icon: 'close',
        label: 'Missing',
        colorClass: 'text-red-500',
        bgClass: 'bg-red-50',
        borderClass: 'border-red-100',
      },
      detected: {
        status: 'detected',
        icon: variant === 'full' ? 'stars' : 'check_circle',
        label: variant === 'full' ? 'Complete' : 'Detected',
        colorClass: 'text-green-600',
        bgClass: 'bg-green-50',
        borderClass: 'border-green-100',
      },
      incomplete: {
        status: 'incomplete',
        icon: 'warning',
        label: 'Incomplete',
        colorClass: 'text-orange-600',
        bgClass: 'bg-orange-50',
        borderClass: 'border-orange-100',
      },
      unknown: {
        status: 'unknown',
        icon: 'help_outline',
        label: 'Unknown',
        colorClass: 'text-gray-500',
        bgClass: 'bg-gray-50',
        borderClass: 'border-gray-100',
      },
    };

    return statusMap[status] || statusMap.unknown;
  }

  /**
   * 获取 Schema.org 状态信息
   * @param {object} schemaDetails - Schema 详细信息
   * @returns {object} 状态信息对象
   */
  static getSchemaStatusInfo(schemaDetails) {
    if (!schemaDetails.exists) {
      return AiCheckerApi.getStatusInfo('missing');
    }

    const qualityMap = {
      high: 'detected',
      medium: 'incomplete',
      low: 'incomplete',
      unknown: 'incomplete',
      none: 'missing',
    };

    return AiCheckerApi.getStatusInfo(qualityMap[schemaDetails.quality]);
  }

  /**
   * 获取数据端点状态信息
   * @param {object} endpointDetails - 端点详细信息
   * @returns {object} 状态信息对象
   */
  static getEndpointStatusInfo(endpointDetails) {
    if (endpointDetails.ndjson.found) {
      return AiCheckerApi.getStatusInfo('detected');
    }
    if (endpointDetails.api.found) {
      return AiCheckerApi.getStatusInfo('incomplete');
    }
    return AiCheckerApi.getStatusInfo('missing');
  }

  /**
   * 根据分数获取评分详情
   * @param {number} score - 分数
   * @param {number} maxScore - 最高分数
   * @returns {object} 评分详情对象
   */
  static getScoreDetail(score, maxScore = 100) {
    const percentage = (score / maxScore) * 100;

    if (percentage >= 75) {
      return { score, percentage, level: 'Excellent', color: 'green', icon: 'star' };
    }
    if (percentage >= 55) {
      return { score, percentage, level: 'Good', color: 'green', icon: 'thumb_up' };
    }
    if (percentage >= 40) {
      return { score, percentage, level: 'Average', color: 'yellow', icon: 'remove' };
    }
    return { score, percentage, level: 'Low', color: 'red', icon: 'warning' };
  }
}

/**
 * 默认导出 API 实例
 */
export const aiCheckerApi = new AiCheckerApi();
